import { first } from 'rxjs/operators';
import { ProformaTemplateService, ProformaTemplate } from '@app/_components/proforma-template';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProformaTemplateComponent } from '../proforma-template/proforma-template.component';
import { Component, Input, OnInit } from '@angular/core';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';

@Component({
    selector: 'app-proforma-template-list',
    templateUrl: './proforma-template-list.component.html',
    styleUrls: ['./proforma-template-list.component.css'],
    providers: [MessageService, ConfirmationService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }

        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
`],
    standalone: false
})
export class ProformaTemplateListComponent implements OnInit {
	constructor(
		private proformaTemplateService: ProformaTemplateService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private dialog: MatDialog,
	) {
		this.statuses = proformaTemplateService.statuses;
	}

	// data
	@Input() showFilter = true;
	@Input() showPagination = true;
	@Input() outlineNewButton = true;
	proformaTemplates: any;
	proformaTemplate: ProformaTemplate;

	// paging
	first = 0;
	rows = 50;

	// list
	datasource: any;
	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	multiSortMeta = [];

	// forms
	formLoading = false;

	statuses: any;

	columns: any[] = [
		{ field: 'proformaTemplateId', header: 'Id', width: '80px' },
		{ field: 'templateName', header: 'Name', width: 'auto' },
		{ field: 'isActive', header: 'Active', width: '100px' },
		{ field: 'baseMgmtFeePCT', header: 'Base Fee %', width: '120px' },
		{ field: 'mgmtFeeFloorPCT', header: 'Floor %', width: '100px' },
		{ field: 'mgmtFeeCeilingPCT', header: 'Ceiling %', width: '100px' },
	];

	ngOnInit(): void {
		try {
			this.multiSortMeta = [];
			this.multiSortMeta.push({ field: 'createDate', order: 0 });
		}
		catch (error) {
			console.log('Error: ' + error);
		}
	}

	onRowDelete(event: any) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + event.proformaTemplateId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete(event);
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	loadLazy(event: LazyLoadEvent) {
		try {
			this.loading = true;
			this.lazyLoadEvent = event;

			if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}

			const pageNumber = Math.round(event.first / event.rows);
			this.proformaTemplateService.findByFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(
					(res: any) => {
						this.recordTotal = res.page.recordTotal;
						this.proformaTemplates = res.data.proformaTemplates;
					},
					(err: any) => {
						this.alertService.error(err);
					},
					() => { this.loading = false; }
				);
		} catch (error) {
			console.log('loadLazy(' + event + ')' + error);
		}
	}

	onRowSelect(event: any) {
		this.onOpenFormEdit(event.data);
	}

	formUpdate(proformaTemplate: ProformaTemplate) {
		try {
			this.formLoading = true;
			this.proformaTemplateService.update(proformaTemplate)
				.subscribe(
					(res: any) => {
						this.proformaTemplates.map((pt, i) => {
							if (pt.proformaTemplateId === res.data.proformaTemplate.proformaTemplateId) {
								this.proformaTemplates[i] = res.data.proformaTemplate;
							}
						});
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
					},
					(err: any) => {
						this.messageService.add({ severity: 'error', summary: JSON.stringify(err), life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	formDelete(proformaTemplate: any) {
		try {
			this.formLoading = true;
			this.proformaTemplateService.delete(proformaTemplate.proformaTemplateId)
				.subscribe(
					(res: any) => {
						this.proformaTemplates.splice(this.proformaTemplates.indexOf(proformaTemplate), 1);
						this.recordTotal = this.proformaTemplates.length;
						this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Template ' + proformaTemplate.proformaTemplateId + ' Deleted', life: 3000 });
					},
					(err: any) => {
						this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Template ' + proformaTemplate.proformaTemplateId + ' could not be deleted.', life: 3000 });
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	onOpenFormAdd() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '900px';
		dialogConfig.data = {
			title: 'Add Proforma Template'
		};

		const dialogRef = this.dialog.open(ProformaTemplateComponent, dialogConfig);

		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				this.addCB(data);
				dialogRef.close();
			});
	}

	onOpenFormEdit(proformaTemplate: ProformaTemplate) {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.width = '900px';
		dialogConfig.data = {
			proformaTemplate: proformaTemplate,
			title: 'Edit Proforma Template'
		};

		const dialogRef = this.dialog.open(ProformaTemplateComponent, dialogConfig);

		dialogRef.componentInstance.doUpdate.subscribe(
			data => {
				this.updateCB(data);
				dialogRef.close();
			});

		dialogRef.componentInstance.doDelete.subscribe(
			data => {
				this.deleteCB(data);
				dialogRef.close();
			});
	}

	addCB(proformaTemplate: ProformaTemplate) {
		if (!this.proformaTemplates) {
			this.proformaTemplates = [];
		}
		this.proformaTemplates.unshift(proformaTemplate);
		this.proformaTemplates = [...this.proformaTemplates];
		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template ' + proformaTemplate.proformaTemplateId + ' Added', life: 3000 });
	}

	updateCB(proformaTemplate: ProformaTemplate) {
		let itemIndex = this.proformaTemplates.findIndex((item: any) => item.proformaTemplateId === proformaTemplate.proformaTemplateId);
		this.proformaTemplates[itemIndex] = proformaTemplate;
		this.proformaTemplates = [...this.proformaTemplates];
		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template ' + proformaTemplate.proformaTemplateId + ' Updated', life: 3000 });
	}

	deleteCB(proformaTemplate: ProformaTemplate) {
		this.proformaTemplates.splice(this.proformaTemplates.indexOf(proformaTemplate), 1);
		this.recordTotal = this.proformaTemplates.length;
		this.proformaTemplates = [...this.proformaTemplates];
		this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template ' + proformaTemplate.proformaTemplateId + ' Deleted', life: 3000 });
	}

	next() { this.first = this.first + this.rows; }
	prev() { this.first = this.first - this.rows; }
	reset() { this.first = 0; }
	isLastPage(): boolean { return this.proformaTemplates ? this.first === (this.proformaTemplates.length - this.rows) : true; }
	isFirstPage(): boolean { return this.proformaTemplates ? this.first === 0 : true; }

	getActiveName(proformaTemplate: any) {
		if (proformaTemplate?.isActive === true) return 'Active';
		if (proformaTemplate?.isActive === false) return 'Inactive';
		return '';
	}

	toPercent(val: any) {
		if (val == null) return '';
		return (val * 100).toFixed(1) + '%';
	}

	reloadPage() {
		window.location.reload();
	}

	showPaginator() {
		return true;
	}
}
